import { useEffect, useId, useRef } from "react";
import videojs from "video.js";
import type Player from "video.js/dist/types/player";
import "video.js/dist/video-js.css";

const MUX_STREAM = "https://stream.mux.com";
const MUX_IMAGE = "https://image.mux.com";

const CONTROL_BAR = {
  pictureInPictureToggle: false,
  subtitlesButton: false,
  chaptersButton: false,
  descriptionsButton: false,
  audioTrackButton: false,
  playbackRateMenuButton: false,
  remainingTimeDisplay: false,
  currentTimeDisplay: false,
  timeDivider: false,
  durationDisplay: false,
} as const;

type VideoPlayerProps = {
  playbackId: string;
  accentColor?: string;
};

export const VideoPlayer = ({
  playbackId,
  accentColor = "#205EA6",
}: VideoPlayerProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  const scopeId = useId().replace(/:/g, "");

  useEffect(() => {
    if (playerRef.current) return;
    const container = containerRef.current;
    if (!container) return;

    const videoEl = document.createElement("video");
    videoEl.className = "video-js vjs-big-play-centered";
    container.appendChild(videoEl);

    playerRef.current = videojs(videoEl, {
      controls: true,
      fluid: true,
      aspectRatio: "16:9",
      poster: `${MUX_IMAGE}/${playbackId}/thumbnail.webp`,
      sources: [
        { src: `${MUX_STREAM}/${playbackId}.m3u8`, type: "application/x-mpegURL" },
      ],
      controlBar: CONTROL_BAR,
    });

    return () => {
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [playbackId]);

  return (
    <div data-vjs-player data-vjs-scope={scopeId}>
      <style>{accentCSS(scopeId, accentColor)}</style>
      <div ref={containerRef} />
    </div>
  );
};

export { MUX_STREAM, MUX_IMAGE, CONTROL_BAR, accentCSS };

function accentCSS(scopeId: string, color: string) {
  return `
    [data-vjs-scope="${scopeId}"] .vjs-big-play-button {
      background-color: ${color}cc;
      border: none;
      border-radius: 50%;
      width: 80px;
      height: 80px;
      line-height: 80px;
      transition: background-color 0.2s;
    }
    [data-vjs-scope="${scopeId}"] .vjs-big-play-button:hover {
      background-color: ${color};
    }
    [data-vjs-scope="${scopeId}"] .vjs-play-progress,
    [data-vjs-scope="${scopeId}"] .vjs-volume-level {
      background-color: ${color};
    }
  `;
}
